import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash, IconView } from '../../components/icons';
import Pagination from '../../components/Pagination';
import { useAuth } from '../../auth/useAuth';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import usePaginatedList from '../../hooks/usePaginatedList';

interface EmployeeRow {
  id: number;
  employee_code: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  mobile: string | null;
  current_position: {
    position: { position_title: string; status: string };
  } | null;
}

export function Employees() {
  const { can } = useAuth();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [search, setSearch] = useState('');

  const canCreate = can('hr.create');
  const canUpdate = can('hr.update');
  const canArchive = can('hr.archive');

  const filters = useMemo(() => ({ search: search || undefined }), [search]);
  const { data, loading, error, setPage, reload: load } = usePaginatedList<EmployeeRow>('/employees', filters);

  const remove = async (employee: EmployeeRow) => {
    if (!(await confirmDelete(`Delete ${employee.first_name} ${employee.last_name}?`))) return;
    try {
      await api.delete(`/employees/${employee.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Employees</h1>
        {canCreate && (
          <Link className="btn btn-primary" to="/hr/employees/new">
            + Add
          </Link>
        )}
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Email</th>
                <th>Current Position</th>
                <th>Mobile</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((e) => (
                  <tr key={e.id}>
                    <td className="muted">{e.employee_code}</td>
                    <td>
                      <strong>
                        {e.first_name} {e.last_name}
                      </strong>
                    </td>
                    <td>{e.email}</td>
                    <td>{e.current_position ? e.current_position.position.position_title : '—'}</td>
                    <td>{e.mobile ?? '—'}</td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/hr/employees/${e.id}/edit`} />
                      {canUpdate && <IconButton icon={IconEdit} label="Edit" to={`/hr/employees/${e.id}/edit`} />}
                      {canArchive && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(e)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No employees found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
